import React from 'react';
import { useTranslation } from 'react-i18next';

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const formatDuration = (totalMinutes) => {
    if (totalMinutes < 60) {
        return `${totalMinutes} min`;
    }

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (minutes === 0) {
        return `${hours} h`;
    }

    return `${hours} h ${minutes} min`;
};

const convertTimeToMinutes = (time) => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

const UserInfoPanel = ({
    selectedStaffInfo,
    selections,
    onDeleteDay,
    onEditDay,
    onSave,
    isSaving,
    saveSuccess
}) => {
    const { t } = useTranslation('schedule');

    if (!selectedStaffInfo || selections.length === 0) return null;

    const weeklyTotal = selections.reduce((total, selection) => total + selection.duration, 0);

    // Short day names following getDay() convention: 0 = Sunday
    const dayNamesShort = [
        t('days.short.sun'), // 0
        t('days.short.mon'), // 1
        t('days.short.tue'), // 2
        t('days.short.wed'), // 3
        t('days.short.thu'), // 4
        t('days.short.fri'), // 5
        t('days.short.sat'), // 6
    ];

    const selectedDaysSummary = selections.reduce((acc, selection) => {
        const existing = acc.find(item => item.dateISO === selection.day);

        if (existing) {
            const selectionStart = selection.startTime;
            const selectionEnd = selection.endTime;
            if (selectionStart < existing.start) existing.start = selectionStart;
            if (selectionEnd > existing.end) existing.end = selectionEnd;
            existing.totalMinutes = convertTimeToMinutes(existing.end) - convertTimeToMinutes(existing.start);
        } else {
            const date = new Date(selection.day);
            const dow = date.getDay();
            const dayLabel = `${dayNamesShort[dow]}, ${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1)
                .toString().padStart(2, '0')}.${date.getFullYear().toString().slice(-2)}`;

            acc.push({
                dayLabel,
                dateISO: selection.day,
                dayOfWeek: dow === 0 ? 7 : dow,
                start: selection.startTime,
                end: selection.endTime,
                totalMinutes: selection.duration
            });
        }
        return acc;
    }, []);

    selectedDaysSummary.sort((a, b) => a.dateISO.localeCompare(b.dateISO));

    return (
        <div
            className="user-info-panel"
            style={{
                padding: '8px 12px',
                margin: '8px 0',
                background: 'linear-gradient(to right, #f3f4f6, #ffffff)',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}
        >
            <div
                className="user-info-panel__header"
                style={{
                    marginBottom: '8px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}
            >
                <h3 className="user-info-panel__name" style={{ margin: 0, fontSize: '14px', fontWeight: '600' }}>
                    {selectedStaffInfo.name}
                </h3>

                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <div className="user-info-panel__total" style={{ fontSize: '13px', fontWeight: '500', color: '#3b82f6' }}>
                        {t('panel.weeklyTotal', { duration: formatDuration(weeklyTotal) })}
                    </div>

                    <button
                        onClick={onSave}
                        disabled={isSaving}
                        style={{
                            padding: '4px 12px',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: 'white',
                            background: saveSuccess ? '#22c55e' : '#3b82f6',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: isSaving ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s',
                            opacity: isSaving ? 0.6 : 1
                        }}
                    >
                        {isSaving
                            ? t('panel.saving')
                            : saveSuccess
                                ? `✓ ${t('panel.saved')}`
                                : t('panel.save')
                        }
                    </button>
                </div>
            </div>

            <div
                className="selected-days-list"
                style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '4px',
                    maxHeight: 'none',
                    overflow: 'visible'
                }}
            >
                {selectedDaysSummary.map((day) => (
                    <div
                        key={day.dateISO}
                        className="selected-day-item"
                        style={{
                            padding: '4px 8px',
                            background: 'white',
                            border: '1px solid #e5e7eb',
                            borderRadius: '6px',
                            fontSize: '11px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            transition: 'all 0.2s'
                        }}
                    >
                        <span className="selected-day-item__day" style={{ fontWeight: '500' }}>
                            {day.dayLabel}
                        </span>
                        <span className="selected-day-item__time" style={{ color: '#6b7280' }}>
                            {day.start}–{day.end}
                        </span>
                        <span
                            className="selected-day-item__edit"
                            onClick={(e) => {
                                e.stopPropagation();
                                onEditDay(day.dateISO, day.start, day.end);
                            }}
                            style={{
                                marginLeft: '2px',
                                color: '#3b82f6',
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontWeight: 'bold',
                                display: 'flex',
                                alignItems: 'center'
                            }}
                            title={t('panel.editTitle')}
                            onMouseEnter={(e) => (e.currentTarget.style.color = '#2563eb')}
                            onMouseLeave={(e) => (e.currentTarget.style.color = '#3b82f6')}
                        >
                            ✎
                        </span>
                        <span
                            className="selected-day-item__delete"
                            onClick={(e) => {
                                e.stopPropagation();
                                onDeleteDay(day.dateISO);
                            }}
                            style={{
                                color: '#ef4444',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center'
                            }}
                            title={t('panel.deleteTitle')}
                            onMouseEnter={(e) => (e.currentTarget.style.color = '#dc2626')}
                            onMouseLeave={(e) => (e.currentTarget.style.color = '#ef4444')}
                        >
                            ×
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

const HelpContainer = () => {
    const { t } = useTranslation('schedule');

    return (
        <div className="help-container" style={{
            padding: '6px 12px',
            background: '#f0f9ff',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            margin: '8px 0',
            fontSize: '12px'
        }}>
            <div className="help-container__icon" style={{ fontSize: '14px' }}>ℹ️</div>
            <p className="help-container__text" style={{ margin: 0, color: '#0369a1' }}>
                {t('panel.helpText')}
            </p>
        </div>
    );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const ScheduleSelectionPanel = ({
    selectedStaffInfo,
    selections,
    onDeleteDay,
    onEditDay,
    onClearAll,
    onSave,
    isSaving,
    saveSuccess
}) => {
    return (
        <div className="schedule-selection-panel" style={{ flexShrink: 0, padding: '0 12px 8px' }}>
            <UserInfoPanel
                selectedStaffInfo={selectedStaffInfo}
                selections={selections}
                onDeleteDay={onDeleteDay}
                onEditDay={onEditDay}
                onSave={onSave}
                isSaving={isSaving}
                saveSuccess={saveSuccess}
            />
            <HelpContainer />
        </div>
    );
};

export default ScheduleSelectionPanel;